<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { defineProps } from "vue";

import ProgressBar from "@/components/explorer/ProgressBar.vue";
import Block from "@/components/ui/atoms/Block.vue";
import Btn from "@/components/ui/atoms/Btn.vue";
import PillContainer from "@/components/ui/atoms/PillContainer.vue";
import type {
  Course,
  Enrollment,
  Masterclass,
  Project,
  Subject,
  StudentNotification,
} from "@/shared/explorerTypes";
import type { User } from "@/shared/types";
import { resolveUrl } from "@/utils/urls";

interface Props {
  course: Course | Project | Masterclass | Subject;
  enrollment?: Enrollment;
  studentNotifications?: StudentNotification[];
  user?: User;
  light?: boolean;
  isFeatured?: boolean;
  noBlocks?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  light: false,
  isFeatured: false,
  noBlocks: false,
});

const countNotifications = (course: Course | Project | Masterclass | Subject) => {
  if (props.studentNotifications) {
    return props.studentNotifications.filter(
      (notification) => notification.subject_id === course.id,
    ).length;
  }
  return 0;
};

const navigateToCourse = (course: Course | Project | Masterclass | Subject) => {
  const user = props.user;

  if (!user) {
    window.location.href = resolveUrl("course_show", course.slug);
  } else if (
    user?.is_content_access === true ||
    (Array.isArray(user?.owner_contents) &&
      user.owner_contents.length > 0 &&
      user.owner_contents.indexOf(course.id) !== -1)
  ) {
    router.get(resolveUrl("player_explorer", course.slug));
  } else if (user && course.type !== "project") {
    window.location.href = resolveUrl("course_explorer_show", course.slug);
  }
};

const goToCourse = (slug: string) => {
  window.location.href = resolveUrl("course_explorer_show", slug);
};

const dictSubject = {
  course: "curso",
  project: "proyecto",
  masterclass: "masterclass",
};
</script>

<template>
  <Block
    theme="smoke"
    tag="article"
    @click="navigateToCourse(course)"
    :class="[
      'subject-card',
      { 'subject-card--light': light },
      { 'subject-card--featured': isFeatured },
      { 'subject-card--no-blocks': noBlocks },
      // { 'subject-card--no-owner': user?.owner_contents.indexOf(course.id) === -1 },
    ]"
  >
    <div class="subject-card__notification" v-if="countNotifications(course) > 0">
      {{ countNotifications(course) }}
    </div>
    <div class="subject-card__details">
      <Block
        class="subject-card__details__img clear"
        :style="`${
          course.poster_image ? 'background-image: url(' + course.poster_image + ')' : ''
        }`"
      />
      <Block class="subject-card__details__info" theme="clear">
        <div class="subject-card__details__info__title-action">
          <h2 class="no-margins size-xl">
            {{ course.name }}
          </h2>
          <PillContainer
            no-padding
            class="subject-card__details__info__action"
            v-if="!light"
          >
            <Btn
              class="sizeless btn-icon explorer-accent"
              shape="round"
              theme="active-bold"
              tag="button"
              v-if="
                user?.is_content_access ||
                (user?.owner_contents?.length > 0 &&
                  user?.owner_contents?.indexOf(course.id) !== -1)
              "
            >
              <span v-if="enrollment?.progress > 0">Continuar</span>
              <span v-else>Iniciar {{ dictSubject[course.type] }}</span>
              <i class="icon icon--black-arrow icon--width-2x" />
            </Btn>
            <Btn
              @click.stop="() => router.get(resolveUrl('explorer_pricing'))"
              class="sizeless btn-icon explorer-accent"
              shape="round"
              theme="active-bold"
              tag="button"
              v-else
            >
              <span>Suscríbete</span>
              <i class="icon icon--black-arrow icon--width-2x" />
            </Btn>
            <Btn
              v-if="course.type !== 'project'"
              class="sizeless btn-icon"
              shape="round"
              theme="dark-wireframe"
              tag="button"
              @click.stop="goToCourse(course.slug)"
            >
              <span>Más detalles</span>
            </Btn>
          </PillContainer>
        </div>

        <div class="subject-card__details__info__instructor" v-if="noBlocks">
          <span class="size-s">Por {{ course.instructor }}</span>
        </div>

        <div class="progress-info" v-if="!light && enrollment">
          <p
            v-if="enrollment.progress !== 0 && enrollment.progress !== 100"
            class="no-margin-bottom size-s"
          >
            Progreso: {{ enrollment.progress }}%
          </p>
          <p
            v-if="enrollment.progress == 100 && enrollment.challenge_completed"
            class="no-margin-bottom size-s"
          >
            ¡Completado!
          </p>
          <p
            v-if="
              enrollment.progress == 100 &&
              !enrollment.challenge_completed &&
              course.type === 'course'
            "
            class="no-margin-bottom size-s warning-text"
          >
            ¡Reto sin completar!
          </p>
          <ProgressBar :progress="enrollment.progress" />
        </div>
      </Block>
    </div>
    <div class="subject-card__blocks" v-if="!noBlocks">
      <Block
        theme="off-white"
        shape="square"
        :class="['subject-card__blocks__item', 'first']"
      >
        <span class="subject-card__blocks__item__title size-xs no-margins">
          TIEMPO ESTIMADO
        </span>
        <h3 class="subject-card__blocks__item__value no-margins">
          {{ course.estimated_time }} horas
        </h3>
      </Block>
      <Block theme="off-white" shape="square" :class="['subject-card__blocks__item']">
        <span class="subject-card__blocks__item__title size-xs no-margins">NIVEL</span>
        <h3 class="subject-card__blocks__item__value no-margins">
          {{ course.level_text }}
        </h3>
      </Block>
      <Block theme="off-white" shape="square" :class="['subject-card__blocks__item']">
        <span class="subject-card__blocks__item__title size-xs no-margins">
          INSTRUCTOR
        </span>
        <h3 class="subject-card__blocks__item__value no-margins">
          {{ course.instructor }}
        </h3>
      </Block>
    </div>
  </Block>
</template>

<style lang="scss" scoped>
@use "@/styles/settings/vars";

h2 {
  span {
    display: inline;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  button {
    display: inline;
    margin-left: 8px;
    vertical-align: middle;
  }
}

.subject-card:hover {
  background: vars.$dust;
  transition: background 0.2s ease-in;
}

.subject-card {
  display: flex;
  position: relative;
  flex-direction: column;
  padding: 1em;
  min-width: 600px;
  max-width: 800px;
  justify-content: space-between;
  cursor: pointer;
  transition: background 0.2s ease-in;

  .explorer-accent {
    border: 1px solid var(--accent-bg);
  }

  .warning-text {
    display: flex;
    align-items: center;
    gap: 0.5em;
  }

  &__notification {
    position: absolute;
    top: -13px;
    right: -13px;
    width: 26px;
    height: 26px;
    background: var(--accent-bg);
    color: var(--text-color);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-weight: bold;
  }

  p {
    color: vars.$off-black;
  }

  &__details {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1em;
    margin-bottom: 0.75em;
    height: 100%;

    > div {
      width: 100%;
    }

    .pill-container {
      padding: 0;
    }

    &__img {
      aspect-ratio: 1/1;
      width: 100%;
      object-fit: cover;
      background-size: cover;
      border-radius: 0.5em;
      background-image: url("@img/default_course_banner.webp");
      max-height: 160px;
      position: relative;
    }

    h2 {
      color: vars.$off-black;
    }

    &__info {
      grid-column: 2 / 4;
      display: flex;
      width: 100%;
      flex-direction: column;
      justify-content: space-between;
      overflow: hidden;
      border-radius: 0;

      &__instructor {
        text-transform: uppercase;
      }

      &__action {
        margin-top: 0.5em;
        gap: 0.5em;
      }
    }
  }

  &__blocks {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1em;

    &__item {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 0.75em;
      gap: 1em;
      border-radius: 0.5em;
    }

    span {
      color: vars.$off-black;
      padding: 0;
    }

    h3 {
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .block {
      flex: 1;
      margin-bottom: 0;
    }
  }
}

@media (min-width: 601px) and (max-width: 800px) {
  article.block {
    .subject-card__details {
      &__img {
        aspect-ratio: auto;
      }
    }
  }

  .block {
    .cluster-info__video-div {
      max-width: none;
    }
  }
  .subjects-container {
    article {
      min-width: 0;

      .subject-card__details {
        max-height: 400px;
        display: grid;
        grid-template-rows: 3fr 1fr;
        grid-template-columns: auto;
      }
    }
  }
}

@media (width > 601px) {
  .subject-card--light .subject-card__details__img {
    max-height: 140px;
  }

  .subject-card--no-blocks .subject-card__details {
    margin-bottom: 0;
  }

  .subject-card--no-blocks .block {
    margin-bottom: 0;
  }
}

@media (width < 600px) {
  .subject-card {
    min-width: 85%;
    max-width: 85%;
    padding: 0.5em;

    &__notification {
      top: -13px;
      right: -6px;
      z-index: 9;
    }

    &__details {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
      gap: 1em;
      align-items: start;

      h2 {
        font-size: 20px;
        line-height: 24px;
        margin-top: 1em;
      }

      &__img {
        width: 100%;
        aspect-ratio: 16/9;
        object-fit: cover;
        border-radius: 0.75em;
        margin-bottom: 0;
      }

      &__info {
        grid-column: 1;
        padding: 0;
        height: 100%;

        &__title-action {
          display: flex;
          flex-direction: column;
        }

        h2 {
          order: 1;
        }

        &__action {
          order: 0;
          margin-top: 0;
        }
      }

      .progress-info {
        p {
          margin-top: 0;
        }
      }

      :has(.progress-info) {
        .subject-card__details__info__title-action {
          h2 {
            margin-top: 1em;
          }
        }
      }
    }
  }

  .subject-card__blocks {
    display: grid;
    grid-auto-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.25em;

    &__item {
      padding: 0.5em;
      gap: 0.5em;

      h3 {
        text-wrap: inherit;
        font-size: 13px;
        line-height: 17px;
      }
    }
  }
}

@media (width > 601px) {
  .subject-card--featured .subject-card__details {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    gap: 1em;
    align-items: start;

    &__img {
      width: 100%;
      aspect-ratio: 16/9;
      object-fit: cover;
      border-radius: 0.75em;
      margin-bottom: 0;
      max-height: none;
    }

    &__info {
      grid-column: 1;
      padding: 0;
      height: 100%;

      &__title-action {
        display: flex;
        flex-direction: column;
      }

      h2 {
        order: 1;
      }

      &__action {
        display: none;
      }
    }
  }
}

.subject-card--project {
  background-color: vars.$off-black;

  &:hover {
    background-color: vars.$storm;
  }

  .subject-card__details__info__title-action h2 {
    color: vars.$off-white;
  }
}

.subject-card--no-owner:hover {
  background-color: vars.$smoke;
}
</style>
